import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Gauge, CalendarDays, Fuel, Settings2, Palette, Armchair, Leaf, MapPin, ShieldCheck, ExternalLink, MessageCircle, Zap } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { km, cvDi, UsatoCard } from "@/components/UsatoCard";
import { useSeo, SITE_URL } from "@/lib/seo";
import { prefillAuto, scrollToId } from "@/lib/smooth";
import { descrizioneUsato } from "@/lib/descrizioneUsato";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const dataIt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : "n.d.");
const FUEL_SCHEMA = { Benzina: "Gasoline", Diesel: "Diesel", Hybrid: "Hybrid", "Plug-in Hybrid": "Plug-in Hybrid", Elettrico: "Electric" };

/** schema.org Car + Offer: solo dati reali presenti nella scheda (niente valori inventati). */
const carSchema = (a, url) => ({
  "@context": "https://schema.org",
  "@type": "Car",
  name: `${a.marca} ${a.modello} ${a.anno || ""}`.trim(),
  description: descrizioneUsato(a),
  url,
  ...(a.foto && { image: a.foto }),
  brand: { "@type": "Brand", name: a.marca },
  model: a.modello,
  ...(a.anno && { vehicleModelDate: String(a.anno) }),
  ...(a.km && { mileageFromOdometer: { "@type": "QuantitativeValue", value: a.km, unitCode: "KMT" } }),
  ...(FUEL_SCHEMA[a.alimentazione] && { fuelType: FUEL_SCHEMA[a.alimentazione] }),
  ...(a.cambio && { vehicleTransmission: a.cambio }),
  ...(a.colore_esterno && { color: a.colore_esterno }),
  ...(cvDi(a) && { vehicleEngine: { "@type": "EngineSpecification", enginePower: { "@type": "QuantitativeValue", value: cvDi(a), unitText: "CV" } } }),
  itemCondition: "https://schema.org/UsedCondition",
  offers: {
    "@type": "Offer",
    url,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/UsedCondition",
    ...(a.prezzo ? { price: a.prezzo, priceCurrency: "EUR" } : {}),
    seller: { "@type": "Organization", name: "XMOVE Italia", url: SITE_URL },
  },
});

const breadcrumbSchema = (nome, url) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Usato", item: `${SITE_URL}/usato/` },
    { "@type": "ListItem", position: 3, name: nome, item: url },
  ],
});

/** Auto uscita dallo stock (410) o slug inesistente (404): pagina "non più disponibile" con alternative, noindex. */
const NonDisponibile = ({ info }) => (
  <div className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-8" data-testid="usato-not-found">
    <div className="mx-auto max-w-3xl text-center">
      <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Usato garantito</p>
      <h1 className="mt-2 font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
        {info?.auto ? `${info.auto.marca} ${info.auto.modello}: non più disponibile` : "Questa auto non è più disponibile"}
      </h1>
      <p className="mt-3 text-sm text-xmove-muted">
        {info?.auto ? `${info.auto.versione}${info.auto.anno ? ` (${info.auto.anno}, ${km(info.auto.km)})` : ""} è appena uscita dallo stock. ` : "Lo stock si aggiorna ogni ora: probabilmente è stata appena venduta. "}
        Qui sotto le alternative più vicine disponibili adesso.
      </p>
      <Link to="/usato/" className="mt-6 inline-block rounded-full bg-xmove-orange px-6 py-3 text-sm font-bold text-white" data-testid="usato-not-found-all">Vedi tutto lo stock</Link>
    </div>
    {info?.simili?.length > 0 && (
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="usato-simili">
        {info.simili.map((a) => <UsatoCard key={a.vehicleid} auto={a} />)}
      </div>
    )}
  </div>
);

export default function UsatoDetail() {
  const { slug } = useParams();
  const [auto, setAuto] = useState(null);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    setAuto(null);
    setErrore(null);
    axios.get(`${API}/usato/${slug}`).then((r) => setAuto(r.data)).catch((e) => setErrore(e.response?.data?.detail || {}));
  }, [slug]);

  const nome = auto ? `${auto.marca} ${auto.modello}` : errore?.auto ? `${errore.auto.marca} ${errore.auto.modello}` : "Auto usata";
  const url = `${SITE_URL}/usato/${slug}/`;
  useSeo({
    title: auto
      ? `${nome} ${auto.anno || ""} usata a noleggio lungo termine, ${km(auto.km)}, ${auto.prezzo ? eur(auto.prezzo) : "prezzo su richiesta"} | XMOVE Italia`
      : `${nome} usata non più disponibile | XMOVE Italia`,
    description: auto
      ? `${auto.versione}. ${auto.anno || ""}, ${km(auto.km)}, ${auto.alimentazione || ""}, ${auto.cambio || ""}${cvDi(auto) ? `, ${cvDi(auto)} CV` : ""}. Noleggio lungo termine usato SecondLife XMOVE, ${auto.prezzo ? `prezzo ${eur(auto.prezzo)}` : "prezzo su richiesta"}.`
      : "Questa auto è uscita dallo stock usato XMOVE: guarda le alternative disponibili adesso.",
    path: `/usato/${slug}/`,
    image: auto?.foto,
    type: auto ? "product" : "website",
    noindex: !auto,
    jsonLd: auto ? [carSchema(auto, url), breadcrumbSchema(nome, url)] : undefined,
  });

  const richiedi = () => {
    prefillAuto(`USATO: ${auto.versione} (${auto.anno}, ${km(auto.km)}, targa ${auto.targa || "n.d."})`);
    scrollToId("usato-form");
  };

  if (errore) {
    return <div className="pb-[72px] md:pb-0"><Header /><NonDisponibile info={errore} /><Footer /><MobileStickyBar /></div>;
  }
  if (!auto) return <div><Header /><div className="pt-40 text-center text-sm text-xmove-muted">Caricamento…</div></div>;

  const dati = [
    { icon: CalendarDays, label: "Immatricolazione", value: dataIt(auto.immatricolazione) },
    { icon: Gauge, label: "Chilometri", value: km(auto.km) },
    { icon: Fuel, label: "Alimentazione", value: auto.alimentazione || "n.d." },
    { icon: Settings2, label: "Cambio", value: auto.cambio || "n.d." },
    { icon: Zap, label: "Potenza", value: cvDi(auto) ? `${cvDi(auto)} CV` : "n.d." },
    { icon: Palette, label: "Colore esterno", value: auto.colore_esterno || "n.d." },
    { icon: Armchair, label: "Interni", value: auto.colore_interno || "n.d." },
    { icon: Leaf, label: "Emissioni CO₂", value: auto.co2 ? `${auto.co2} g/km` : "n.d." },
    { icon: ShieldCheck, label: "Neopatentati", value: auto.neopatentati === "SI" ? "Sì" : auto.neopatentati === "NO" ? "No" : "n.d." },
  ];

  return (
    <div className="pb-[72px] md:pb-0" data-testid="usato-detail-page">
      <Header />
      <div className="mx-auto max-w-7xl px-5 pt-28 lg:px-8 lg:pt-32">
        <nav className="flex items-center gap-1.5 text-xs text-xmove-muted" data-testid="usato-breadcrumb">
          <Link to="/" className="hover:text-xmove-orange">Home</Link><ChevronRight size={12} />
          <Link to="/usato/" className="hover:text-xmove-orange" data-testid="breadcrumb-usato">Usato</Link><ChevronRight size={12} />
          <span className="text-xmove-ink">{nome}</span>
        </nav>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">NLT SecondLife · {auto.segmento?.split(" - ")[1] || "Auto"}</p>
            <h1 className="mt-2 font-display text-4xl font-bold leading-tight text-xmove-ink sm:text-5xl">{nome}</h1>
            <p className="mt-3 text-sm text-xmove-muted md:text-base">{auto.versione}</p>

            <div className="mt-8 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-xmove-night shadow-soft" data-testid="usato-player">
              {auto.foto ? (
                <img src={auto.foto} alt={nome} className="aspect-[16/10] w-full object-cover" data-testid="usato-foto-principale" />
              ) : auto.link_foto ? (
                <iframe src={auto.link_foto} title={`Foto e video ${nome}`} className="aspect-[16/10] w-full" allow="fullscreen" loading="lazy" />
              ) : (
                <div className="flex aspect-[16/10] items-center justify-center text-sm text-white/60">Foto in arrivo</div>
              )}
            </div>
            {auto.link_foto && (
              <a href={auto.link_foto} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-xmove-muted hover:text-xmove-orange" data-testid="usato-player-link">
                <ExternalLink size={12} /> {auto.foto ? "Guarda tutte le foto e il video a 360°" : "Apri foto e video a schermo intero"}
              </a>
            )}

            <h2 className="mt-10 text-base font-bold uppercase tracking-wider text-xmove-ink md:text-lg">Descrizione</h2>
            <p className="mt-4 text-base leading-relaxed text-xmove-muted" data-testid="usato-descrizione">{descrizioneUsato(auto)}</p>

            <h2 className="mt-10 text-base font-bold uppercase tracking-wider text-xmove-ink md:text-lg">Scheda tecnica</h2>
            <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="usato-dati">
              {dati.map((d) => (
                <div key={d.label} className="rounded-xl border border-xmove-ink/8 bg-white p-4">
                  <dt className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-xmove-muted"><d.icon size={13} className="text-xmove-orange" /> {d.label}</dt>
                  <dd className="mt-1.5 text-sm font-semibold text-xmove-ink">{d.value}</dd>
                </div>
              ))}
            </dl>

            {auto.optional?.length > 0 && (
              <>
                <h2 className="mt-10 text-base font-bold uppercase tracking-wider text-xmove-ink md:text-lg">Optional e dotazioni</h2>
                <ul className="mt-4 flex flex-wrap gap-2" data-testid="usato-optional">
                  {auto.optional.map((o) => <li key={o} className="rounded-full border border-xmove-ink/10 bg-white px-3 py-1.5 text-xs text-xmove-ink">{o}</li>)}
                </ul>
              </>
            )}
            {auto.note && <p className="mt-6 rounded-xl bg-xmove-orange/10 px-4 py-3 text-sm font-semibold text-xmove-ink" data-testid="usato-note">{auto.note}</p>}
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft" data-testid="usato-prezzo-box">
              {auto.prezzo ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-xmove-muted">Prezzo chiavi in mano</p>
                  <p className="mt-1 font-display text-4xl font-bold text-xmove-ink" data-testid="usato-prezzo">{eur(auto.prezzo)}</p>
                  {auto.esempio_quotazione && (
                    <div className="mt-4 rounded-xl bg-xmove-cream p-4" data-testid="usato-esempio">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-xmove-orange">Esempio di quotazione</p>
                      <p className="mt-1 whitespace-pre-line text-sm text-xmove-ink">{auto.esempio_quotazione}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-xmove-muted">Prezzo</p>
                  <p className="mt-1 font-display text-2xl font-bold text-xmove-orange" data-testid="usato-su-richiesta">Prezzo su richiesta</p>
                  <p className="mt-2 text-sm text-xmove-muted">Contattaci per avere informazioni su prezzo, disponibilità e formule di pagamento o noleggio.</p>
                </>
              )}
              <ul className="mt-5 space-y-2 text-sm text-xmove-ink">
                <li className="flex items-center gap-2"><ShieldCheck size={15} className="text-xmove-green" /> Garanzia e revisione inclusa</li>
                <li className="flex items-center gap-2"><Gauge size={15} className="text-xmove-green" /> Km certificati al {auto.data_km ? new Date(auto.data_km).toLocaleDateString("it-IT") : "ritiro"}</li>
                {auto.disponibile_dal && <li className="flex items-center gap-2"><MapPin size={15} className="text-xmove-green" /> Disponibile dal {new Date(auto.disponibile_dal).toLocaleDateString("it-IT")}</li>}
              </ul>
              <button onClick={richiedi} data-testid="usato-richiedi-btn" className="mt-6 w-full rounded-full bg-xmove-orange px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-xmove-orangeDark">
                Richiedi informazioni
              </button>
              <a href={`https://wa.me/393793792323?text=${encodeURIComponent(`Ciao, sono interessato all'usato ${auto.versione} (${auto.targa || ""})`)}`} target="_blank" rel="noreferrer" data-testid="usato-whatsapp" className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-xmove-ink/15 px-6 py-3 text-sm font-bold text-xmove-ink transition-colors hover:border-xmove-green hover:text-xmove-green">
                <MessageCircle size={15} /> Scrivici su WhatsApp
              </a>
            </div>
            <div id="usato-form" className="mt-6 scroll-mt-28 rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft">
              <h2 className="text-base font-bold text-xmove-ink md:text-lg">Ti richiamiamo noi</h2>
              <p className="mb-4 mt-1 text-xs text-xmove-muted">Lascia i tuoi contatti: rispondiamo entro poche ore lavorative.</p>
              <LeadForm idPrefix="usato" compact />
            </div>
          </aside>
        </div>
      </div>
      <div className="mt-20"><Footer /></div>
      <MobileStickyBar />
    </div>
  );
}
